import React, { useEffect, useMemo, useState } from "react";
import { useMediaQuery } from "react-responsive";
import { useNavigate } from "react-router-dom";
import { useRecoilState, useResetRecoilState } from "recoil";
import "./index.scss";
import { ReactComponent as CloseIcon } from "../../static/icons/close.svg";
import {
  CounselingField,
  requestState,
  UsePeriod,
} from "../../store/RequestStore";
import { RequestDialog } from "../../components/consult";
import { useLocalStorage } from "../../shared/utill";

type OfficeOption = {
  id: string;
  title: string;
  description: string;
  price: number; // 정가(월). 할인가는 DISCOUNT_PERCENT로 계산한다.
  counselingType: CounselingField;
};

// 선착순 웰컴 이벤트: 반값(50%) 할인.
const DISCOUNT_PERCENT = 50;

const formatMonthlyPrice = (value: number) =>
  `${value.toLocaleString("ko-KR")}원 / 월`;

const getDiscountedPrice = (price: number) =>
  Math.round((price * (100 - DISCOUNT_PERCENT)) / 100);

const Consult3 = () => {
  const [request, setRequest] = useRecoilState(requestState);
  const resetRequest = useResetRecoilState(requestState);
  const [selectedOption, setSelectedOption] = useState<OfficeOption | null>(
    null
  );
  const [dialogVisible, setDialogVisible] = useState(false);
  const [, setPersistedRequest] = useLocalStorage("request", request);

  const navigate = useNavigate();
  const isMobile = useMediaQuery({
    query: "(max-width: 1200px)",
  });

  useEffect(() => resetRequest(), [resetRequest]);
  useEffect(() => {
    if ((window as any).fbq) {
      (window as any).fbq("track", "Optimization1");
    }
  }, []);
  useEffect(() => {
    if ((window as any).karrotPixel) {
      (window as any).karrotPixel.track("ViewContent");
    }
  }, []);
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" as ScrollBehavior });
  }, []);

  const officeOptions = useMemo<OfficeOption[]>(
    () => [
      {
        id: "single-basic",
        title: "1인실 Basic",
        description: "독립 공간",
        price: 250000,
        counselingType: CounselingField.SINGLE_BASIC,
      },
      {
        id: "single-premium",
        title: "1인실 Premium",
        description: "독립 공간, 창가 자리 or 넓은 공간",
        price: 300000,
        counselingType: CounselingField.SINGLE_PREMIUM,
      },
      {
        id: "double-basic",
        title: "2인실 Basic",
        description: "독립 공간",
        price: 400000,
        counselingType: CounselingField.DOUBLE_BASIC,
      },
      {
        id: "double-premium",
        title: "2인실 Premium",
        description: "독립 공간, 창가 자리 or 넓은 공간",
        price: 500000,
        counselingType: CounselingField.DOUBLE_PREMIUM,
      },
      {
        id: "open-seat",
        title: "오픈석(지정)",
        description: "지정된 개방 공간",
        price: 180000,
        counselingType: CounselingField.OPEN_SEAT,
      },
    ],
    []
  );

  const handleOptionClick = (option: OfficeOption) => {
    setSelectedOption(option);
    const updatedRequest = {
      ...request,
      counselingType: option.counselingType,
      usePeriod: UsePeriod._3,
    };
    setRequest(updatedRequest);
    setPersistedRequest(updatedRequest);
    const script = document.createElement("script");
    script.type = "text/javascript";
    script.text = `
      var across_adn_complete_contain = new fn_across_adn_contain();
      var across_adn_complete_param = [];
      across_adn_complete_param = {"ut":"Complete","ui":"107325","uo":"types1"};
      across_adn_complete_contain.init(across_adn_complete_param);
    `;
    document.body.appendChild(script);
    window.scrollTo({ top: 0, behavior: "instant" as ScrollBehavior });
    setDialogVisible(true);
  };

  const handleDialogClose = () => {
    setDialogVisible(false);
  };

  const handleSubmitSuccess = () => {
    setDialogVisible(false);
    navigate("/");
  };

  useEffect(() => {
    if (dialogVisible) {
      window.scrollTo({ top: 0, behavior: "instant" as ScrollBehavior });
    }
  }, [dialogVisible]);

  return (
    <div className="consult consult-1">
      <div className="header-page-title-wrapper">
        <div className="back-button" onClick={() => navigate("/private-office")}>
          <CloseIcon />
        </div>
        {isMobile && <div className="dummy"></div>}
      </div>
      <div className="consult-content w-full px-0 md:flex md:justify-center md:px-5">
        <div className="content flex w-full flex-col items-center gap-10 md:max-w-[506px] 1200:pt-[30px]">
          <div className="flex w-full flex-col items-center gap-4">
            <div className="flex items-center justify-center rounded-full bg-[#2776ff] px-[14px] py-[6px]">
              <span className="whitespace-nowrap text-[15px] font-semibold leading-[1.4] text-white">
                선착순 웰컴 이벤트
              </span>
            </div>
            <div className="flex flex-col items-center gap-[6px] text-center tracking-[-0.875px] text-[#454f5d]">
              <p className="text-[26px] font-bold leading-[1.4] md:text-[32px]">
                새로운 시작을 부담없이!
              </p>
              <p className="text-[15px] font-semibold leading-[1.4] md:text-[18px]">
                <span className="text-[#566476]">
                  6개월 계약 시 첫 3개월, 12개월 계약 시 첫 6개월
                </span>
                <br />
                <span className="font-bold text-[#2776ff]">
                  50% 선착순 할인 혜택
                </span>
              </p>
            </div>
          </div>
          <div className="flex w-full flex-col items-end gap-4">
            <div className="flex w-full flex-col gap-3 md:gap-4">
              {officeOptions.map((option) => {
                const discountedPrice = getDiscountedPrice(option.price);
                return (
                  <button
                    key={option.id}
                    type="button"
                    onClick={() => handleOptionClick(option)}
                    aria-pressed={selectedOption?.id === option.id}
                    className="relative flex min-h-[78px] w-full items-center gap-3 rounded-[20px] bg-white px-5 py-4 text-left shadow-[0_2px_10px_rgba(24,33,50,0.06)] transition-shadow duration-200 hover:shadow-[0_4px_20px_rgba(53,49,56,0.1)] md:px-8 md:py-6"
                  >
                    <div className="flex min-w-0 flex-1 flex-col gap-[3px] text-left">
                      <div className="whitespace-nowrap text-[18px] font-bold leading-[1.4] tracking-[-0.875px] text-[#454f5d]">
                        {option.title}
                      </div>
                      <div className="text-[12px] font-semibold leading-[1.4] tracking-[-0.875px] text-[#a2a7ae]">
                        {option.description}
                      </div>
                    </div>
                    <div className="flex items-center gap-3 540r:flex-col 540r:items-end 540r:gap-1">
                      <div className="flex h-[28px] shrink-0 items-center justify-center rounded-[50px] bg-[#d9e9ff] px-[10px]">
                        <span className="whitespace-nowrap text-[13px] font-semibold leading-none tracking-[-0.13px] text-[#2776ff]">
                          선착순 반값 혜택
                        </span>
                      </div>
                      <div className="flex flex-col items-end justify-center gap-[3px] tracking-[-0.875px]">
                        <p className="whitespace-nowrap text-[18px] font-bold leading-[1.4] text-[#2776ff]">
                          {formatMonthlyPrice(discountedPrice)}
                        </p>
                        <p className="whitespace-nowrap text-[14px] font-semibold leading-[1.4] text-[#a2a7ae] line-through">
                          {formatMonthlyPrice(option.price)}
                        </p>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
            <div className="w-full text-right text-[12px] font-medium leading-none tracking-[-0.8px] text-[#949ca8]">
              VAT 별도
            </div>
          </div>
        </div>
      </div>
      <RequestDialog
        visible={dialogVisible}
        onClose={handleDialogClose}
        onSubmitSuccess={handleSubmitSuccess}
        defaultArea="경기 동탄점"
        isAreaFixed
      />
    </div>
  );
};

export default Consult3;
